import { Injectable } from '@angular/core';
import { ModalService } from './modal.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { WatchSharingModalComponent } from 'app/modules/browse/pages/scenic/components/watch-sharing-modal/watch-sharing-modal.component';
import { WatchIntegrateModal } from 'app/modules/browse/pages/scenic/components/watchIntegrateModal/watchIntegrateModal.component';
import { WatchPlaylistModal } from 'app/modules/browse/pages/scenic/components/watchPlaylistModal/watchPlaylistModal.component';
import { WatchNewPlaylistModalComponent } from 'app/modules/browse/pages/scenic/components/watchNewPlaylistModal/watchNewPlaylistModal.component';
import { TranslateService } from '@ngx-translate/core';
import { WatchLikeList } from 'app/modules/browse/pages/scenic/components/watchLikeList/watchLikeList.component';
import { WatchReportModal } from 'app/modules/browse/pages/scenic/components/watchReportModal/watchReportModal.component';
import { User } from 'app/models/user';
import { WatchRankingModal } from 'app/modules/browse/pages/watch/components/watchRankingModal/watchRankingModal.component';
import { Top3 } from '@kinomap-dev/ng-ui-lib';
import { ToasterService } from './toaster.service';

@Injectable({
  providedIn: 'root',
})
export class WatchActionsService {
  modalSubscription: Subscription;

  constructor(
    private modal: ModalService,
    private toasterService: ToasterService,
    private translate: TranslateService
  ) {}

  /**
   * Permet de déclancher la modal de partage.
   */
  shareModal(): void {
    const modalClass: string = 'sharing-modal';
    this.modal.display(modalClass);
    this.modalSubscription?.unsubscribe();

    setTimeout(() => {
      this.modal.setDialogComponent(WatchSharingModalComponent);
    });

    this.modal.onModalClose.subscribe(() => {
      const component: WatchSharingModalComponent = this.modal
        .dialogComponent as WatchSharingModalComponent;
      component.copied = false;
    });
  }

  /**
   * Permet de déclancher la modal d'integration de l'iframe.
   * @param videoId: number | string
   */
  integrateModal(videoHashId: string): void {
    const modalClass: string = 'integrate__modal';
    this.modal.display(modalClass);
    this.modalSubscription?.unsubscribe();

    setTimeout(() => {
      this.modal.setDialogComponent(WatchIntegrateModal);
    });

    this.modalSubscription = this.modal.onModalRendered.subscribe(
      (component: WatchIntegrateModal) => {
        component.videoHashId = videoHashId;
      }
    );
  }

  /**
   * Permet d'afficher la modal des playlists.
   *
   * @param type: 'watch' | 'coaching'
   * @param user: User | null
   * @param videoId: string | number
   * @param isApprouved:boolean = true (true par default)
   * @returns
   */
  playlistModal(
    type: 'watch' | 'coaching',
    user: User | null,
    videoId: string | number,
    isApprouved: boolean = true
  ): void {
    const id: number =
      typeof videoId === 'string' ? parseInt(videoId) : videoId;

    if (!user) {
      this.toasterService.warning(null, 'you need to be logged.');
      return;
    } else if (!isApprouved) {
      this.toasterService.warning(
        null,
        this.translate.instant('watch.video_player.video_not_accepted')
      );
      return;
    }

    const modalClass: string = 'playlist-modal';
    this.modal.display(modalClass);
    this.modalSubscription?.unsubscribe();

    setTimeout(() => {
      this.modal.setDialogComponent(WatchPlaylistModal);
    });

    if (this.modal.dialogComponent instanceof WatchPlaylistModal) {
      const component: WatchPlaylistModal = this.modal
        .dialogComponent as WatchPlaylistModal;
      component.videoId = id;
      component.type = type;
      component.closeModal.subscribe(() => {
        this.modal.hide();
      });

      component.newPlaylist.subscribe(() => {
        this.modal.setDialogComponent(WatchNewPlaylistModalComponent);
      });
      return;
    }

    this.modalSubscription = this.modal.onModalRendered.subscribe(
      (component: WatchPlaylistModal) => {
        component.videoId = id;
        component.type = 'watch';
        component.scrollContainerSelector = `.${modalClass} div.p-dialog-content`;
        component.closeModal.subscribe(() => {
          this.modal.hide();
        });

        component.newPlaylist.subscribe(() => {
          component.videoId = id;
          this.modal.setDialogComponent(WatchNewPlaylistModalComponent);
          this.modalSubscription = this.modal.onModalRendered.subscribe(
            (component: WatchNewPlaylistModalComponent) => {
              component.close.subscribe((playlistName: string) => {
                this.modal.hide();
                this.toasterService.success(
                  null,
                  playlistName +
                    ' ' +
                    this.translate.instant(
                      'watch.playlist_modal.create_new_playlist_modal.toast'
                    )
                );
              });
            }
          );
        });
      }
    );
  }

  /**
   * Permet d'ouvrir la modal de like.
   * @param type : 'watch' | 'coaching' | 'activity' | 'mapmode';
   * @param user : User | null;
   * @param videoId :  string | number;
   * @param title : string
   * @param isLiked : BehaviorSubject<boolean>;
   * @param likeCount : BehaviorSubject<number>
   * @returns un observable du click sur le bouton like (dans la modale);
   */
  likesListModal(
    type: 'watch' | 'coaching' | 'activity' | 'mapmode',
    user: User | null,
    videoId: string | number,
    title: string,
    isLiked: BehaviorSubject<boolean>,
    likeCount: BehaviorSubject<number>
  ): BehaviorSubject<void> {
    const id: number =
      typeof videoId === 'string' ? parseInt(videoId) : videoId;
    const likeClick$: BehaviorSubject<void> = new BehaviorSubject<void>(null);

    const likeModalClass: string = 'list-like__modal';
    this.modal.display(likeModalClass);
    this.modalSubscription?.unsubscribe();

    setTimeout(() => {
      this.modal.setDialogComponent(WatchLikeList);
    });

    if (!(this.modal.dialogComponent instanceof WatchLikeList)) {
      this.modalSubscription = this.modal.onModalRendered.subscribe(
        (component: WatchLikeList) => {
          component.scrollContainerSelector = `.${likeModalClass} div.p-dialog-content`;
          component.title = title;
          component.liked = isLiked;
          component.likeCount = likeCount;
          component.currentUser = user;
          component.type = type;
          component.videoId = id;
          component.likeClick.subscribe((res: any) => {
            likeClick$.next(res);
          });
        }
      );
    }
    return likeClick$;
  }

  /**
   *
   * @param gpsOnlyId
   * @param blob
   */
  downloadGPX(gpsOnlyId: number, blob: Blob): void {
    const _blob: Blob = new Blob([blob], {
      type: 'application/octet-stream',
    });
    const link: HTMLAnchorElement = document.createElement('a');
    link.href = window.URL.createObjectURL(_blob);
    link.download = `kinomap-${gpsOnlyId}.gpx`;
    link.click();
    window.URL.revokeObjectURL(link.href);
    this.toasterService.success(
      null,
      `Le fichier kinomap-${gpsOnlyId}.gpx a été correctement téléchargé.`
    );
  }

  /**
   *
   * @param videoId
   */
  reportModal(videoId: number): void {
    this.modal.display('report-video-modal');
    this.modalSubscription?.unsubscribe();

    setTimeout(() => {
      this.modal.setDialogComponent(WatchReportModal);
    });

    // reset the modal on closed
    if (this.modal.dialogComponent instanceof WatchReportModal) {
      const component: WatchReportModal = this.modal
        .dialogComponent as WatchReportModal;
      component.videoReport = true;
      return;
    }
    this.modalSubscription = this.modal.onModalRendered.subscribe(
      (component: WatchReportModal) => {
        component.id = videoId;
        component.type = 'watch';
        component.videoReport = true;
        component.cancel.subscribe(() => {
          this.modal.hide();
        });
      }
    );
  }

  rankingModal(
    activityType: 'scenic' | 'gps-only',
    activityId: number,
    user: User | null,
    sport: string,
    top3: Top3 | null
  ) {
    const modalClass: string = 'ranking__modal';
    this.modal.display(modalClass);
    this.modalSubscription?.unsubscribe();

    setTimeout(() => {
      this.modal.setDialogComponent(WatchRankingModal);
    });

    if (this.modal.dialogComponent instanceof WatchRankingModal) {
      const component = this.modal.dialogComponent as WatchRankingModal;
    } else {
      this.modalSubscription = this.modal.onModalRendered.subscribe(
        (component: WatchRankingModal) => {
          component.videoId = activityId;
          component.userLogged = user;
          component.userRanked = top3.me;
          component.top3 = top3;
          component.scrollContainerSelector = `.${modalClass} div.p-dialog-content`;
          component.sport = sport;
          component.activityType = activityType;
        }
      );
    }
  }
}
